import { createContext, useContext, useMemo } from 'react';
import { ProductTypeForTracking } from '../../lib/gtm';
import { useSearchParams } from 'react-router-dom';
import { DocumentLanguageDetector } from '@n2-common/lib/DocumentLanguageDetector';
import { getApplicatId } from '@n2-common/services/legacyApplicationApiService';

export type BaseApplicationProperties = { antiforgeryToken?: string; productType: ProductTypeForTracking; country: Country };
type ApplicationIdProps = {
    applicationId?: string;
    applicantId?: string;
};

export const ApplicationIdContext = createContext<(ApplicationIdProps & BaseApplicationProperties) | null>(null);

type AssertedApplicationIdProps<TRequireApplicationId, TRequireApplicantId> = {
    applicationId: TRequireApplicationId extends true ? string : string | undefined;
    applicantId: TRequireApplicantId extends true ? string : string | undefined;
};

export function useApplicationIdContext<TRequireApplicationId extends boolean = true, TRequireApplicantId extends boolean = true>(
    requireApplicantId?: TRequireApplicantId,
    requireApplicationId?: TRequireApplicationId
): AssertedApplicationIdProps<TRequireApplicationId, TRequireApplicantId> & BaseApplicationProperties {
    const res = useContext(ApplicationIdContext);
    if (res === null) {
        throw new Error('useApplicationIdContext hook is only allowed to be used nested under a ApplicationIdContext.Provider.');
    }
    assertApplicationId(res, requireApplicationId === false ? false : true);
    assertApplicantId(res, requireApplicantId === false ? false : true);

    return res as AssertedApplicationIdProps<TRequireApplicationId, TRequireApplicantId> & BaseApplicationProperties;
}

function assertApplicationId<TRequired extends boolean>(
    props: ApplicationIdProps,
    requireApplicationId: TRequired
): asserts props is TRequired extends true ? ApplicationIdProps & { applicantionId: string } : ApplicationIdProps {
    if (requireApplicationId && !props.applicationId) {
        throw new Error('ApplicationId is undefined');
    }
}

function assertApplicantId<TRequired extends boolean>(
    props: ApplicationIdProps,
    requireApplicantId: TRequired
): asserts props is TRequired extends true ? ApplicationIdProps & { applicantId: string } : ApplicationIdProps {
    if (requireApplicantId && !props.applicantId) {
        throw new Error('ApplicantId is undefined');
    }
}

export function ApplicationContextProvider({
    children,
    antiforgeryToken,
    country,
    productType
}: BaseApplicationProperties & ApplicationIdProps & React.PropsWithChildren) {
    const [searchParams] = useSearchParams();

    const values = useMemo(
        () => ({
            applicantId: searchParams.get('applicantId') || undefined,
            applicationId: searchParams.get('applicationId') || undefined,
            antiforgeryToken,
            country,
            productType
        }),
        [antiforgeryToken, searchParams, country, productType]
    );

    return <ApplicationIdContext.Provider value={values}>{children}</ApplicationIdContext.Provider>;
}

export async function getApplicationIds(request: Request) {
    const { searchParams } = new URL(request.url);
    const applicationId = searchParams.get('applicationId');
    if (!applicationId) {
        throw new Error('No applicationId in request');
    }
    let applicantId = searchParams.get('applicantId');
    if (!applicantId) {
        applicantId = (await getApplicatId(applicationId)).ApplicantId;
    }
    if (!applicantId) {
        throw new Error(`Cant find applicantId for application: ${applicationId}`);
    }
    return {
        applicationId,
        applicantId,
        country: new DocumentLanguageDetector().detect().toUpperCase() as Country,
    };
}
